import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Badge, Button, Dialog, Field, KeyValue, Section, Select, Spinner, Switch, Textarea } from '@zt/ui';
import { api, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type {
  BizimhesapCollection,
  BizimhesapCorrection,
  BizimhesapLinkPreview,
  BizimhesapSettings,
} from '../lib/types';

const CASH_KEYS = ['card', 'bank_transfer', 'cash_on_delivery', 'manual', 'cash'] as const;
type CashKey = (typeof CASH_KEYS)[number];

const collectionTone = {
  pending: 'slate',
  sending: 'amber',
  sent: 'moss',
  failed: 'rose',
  uncertain: 'amber',
  cancelled: 'slate',
} as const;

/**
 * BizimHesap cari senkronu (F7-25, ADR-113). Cari hesabın ana kaydı BizimHesap'tır: bayi siparişinin faturası
 * bayinin cari koduna, ödenmiş siparişin tahsilatı eşlenen kasaya yazılır. BizimHesap'a yazan adımlar varsayılan
 * kapalıdır.
 */
export function BizimhesapPage() {
  const t = useT();
  const q = useQuery({
    queryKey: ['bizimhesap'],
    queryFn: () => api.get<BizimhesapSettings>('/v1/admin/bizimhesap'),
  });
  return (
    <PageFrame helpKey="page.bizimhesap">
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : (
        <>
          {!q.data.connected && (
            <p role="status" className="rounded-md border border-line bg-surface-2 px-3 py-2 text-sm">
              {t('admin.bizimhesap.notConnected')}{' '}
              <Link to="/entegrasyonlar" className="text-pine underline underline-offset-2">
                {t('admin.nav.integrations')}
              </Link>
            </p>
          )}
          <CashSection key={`cash-${q.data.version}`} data={q.data} />
          <LinksSection data={q.data} />
          <WritesSection key={`writes-${q.data.version}`} data={q.data} />
          <CorrectionsSection data={q.data} />
          <CollectionsSection data={q.data} />
          <SamplesSection data={q.data} />
        </>
      )}
    </PageFrame>
  );
}

function CashSection({ data }: { data: BizimhesapSettings }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const manage = can('org.integration.manage');
  const [map, setMap] = useState<Record<CashKey, string>>(
    () => Object.fromEntries(CASH_KEYS.map((k) => [k, data.cashMap[k] ?? ''])) as Record<CashKey, string>,
  );
  const refresh = useApiMutation(() => api.post('/v1/admin/bizimhesap/cashiers/refresh', {}), {
    invalidate: [['bizimhesap']],
    success: t('admin.bizimhesap.cashiersRefreshed'),
  });
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/bizimhesap/cash-map', {
        ...Object.fromEntries(CASH_KEYS.map((k) => [k, map[k] || null])),
        version: data.version,
      }),
    { invalidate: [['bizimhesap']], success: t('common.saved'), silentFieldErrors: true },
  );
  const changed = CASH_KEYS.some((k) => (data.cashMap[k] ?? '') !== map[k]);
  return (
    <Section
      title={t('admin.bizimhesap.cashTitle')}
      description={t('admin.bizimhesap.cashLead')}
      action={<HelpTip k="bizimhesap.cash_map" />}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2 text-sm text-ink-2">
        <span>
          {data.cashiersFetchedAt
            ? t('admin.bizimhesap.cashiersAt', {
                count: data.cashiers.length,
                date: formatDate(data.cashiersFetchedAt, true),
              })
            : t('admin.bizimhesap.cashiersNever')}
        </span>
        {manage && (
          <span className="flex items-center gap-1">
            <Button
              size="sm"
              loading={refresh.isPending}
              disabled={!data.connected}
              onClick={() => refresh.mutate(undefined)}
            >
              {t('admin.bizimhesap.refreshCashiers')}
            </Button>
            <HelpTip k="bizimhesap.refresh_cashiers" />
          </span>
        )}
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {CASH_KEYS.map((k) => (
          <Field
            key={k}
            label={t(`admin.bizimhesap.cashKeys.${k}`)}
            help={<HelpTip k={`bizimhesap.cash_${k}`} />}
            error={fieldError(save.error, k)}
          >
            {(p) => (
              <Select
                {...p}
                value={map[k]}
                disabled={!manage || !data.cashiers.length}
                onChange={(e) => setMap({ ...map, [k]: e.target.value })}
              >
                <option value="">{t('admin.bizimhesap.noCash')}</option>
                {data.cashiers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.title}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        ))}
      </div>
      <p className="mt-3 text-sm text-ink-3">{t('admin.bizimhesap.cashRule')}</p>
      {manage && (
        <div className="mt-3 flex items-center gap-1">
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!changed}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.bizimhesap.saveCash')}
          </Button>
          <HelpTip k="bizimhesap.save_cash" />
        </div>
      )}
    </Section>
  );
}

function LinksSection({ data }: { data: BizimhesapSettings }) {
  const t = useT();
  const { can, dealerScoped } = usePermissions();
  const manage = can('org.integration.manage') && !dealerScoped;
  const [preview, setPreview] = useState<BizimhesapLinkPreview | null>(null);
  const load = useApiMutation(
    () => api.post<BizimhesapLinkPreview>('/v1/admin/bizimhesap/customer-links/preview', {}),
    { onSuccess: setPreview },
  );
  return (
    <Section
      title={t('admin.bizimhesap.linksTitle')}
      description={t('admin.bizimhesap.linksLead')}
      action={<HelpTip k="bizimhesap.links" />}
    >
      <KeyValue
        items={[
          { label: t('admin.bizimhesap.dealers'), value: data.dealers },
          { label: t('admin.bizimhesap.linked'), value: data.linked },
          {
            label: t('admin.bizimhesap.customersCheck'),
            value: data.customersCheck
              ? t('admin.bizimhesap.customersCheckResult', { missing: data.customersCheck.missing.length })
              : '—',
          },
        ]}
      />
      {manage && (
        <div className="mt-3 flex items-center gap-1">
          <Button loading={load.isPending} disabled={!data.connected} onClick={() => load.mutate(undefined)}>
            {t('admin.bizimhesap.importCustomers')}
          </Button>
          <HelpTip k="bizimhesap.import_customers" />
        </div>
      )}
      {preview && <LinkDialog preview={preview} onClose={() => setPreview(null)} />}
    </Section>
  );
}

function LinkDialog({ preview, onClose }: { preview: BizimhesapLinkPreview; onClose: () => void }) {
  const t = useT();
  // Önerilen eşleşmeler seçili gelir; belirsizlerde aday seçilir.
  const [chosen, setChosen] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      preview.rows
        .filter((r) => (r.status === 'match_code' || r.status === 'match_tax') && r.candidate?.code)
        .map((r) => [r.companyId, r.candidate!.code!]),
    ),
  );
  const apply = useApiMutation(
    () =>
      api.post<{ linked: number }>('/v1/admin/bizimhesap/customer-links', {
        links: Object.entries(chosen)
          .filter(([, code]) => code)
          .map(([companyId, code]) => ({ companyId, code })),
      }),
    {
      invalidate: [['bizimhesap'], ['company'], ['companies'], ['ledger']],
      onSuccess: (r) => {
        toast.success(t('admin.bizimhesap.linkedDone', { count: r.linked }));
        onClose();
      },
    },
  );
  const groups = useMemo(
    () => ({
      matched: preview.rows.filter((r) => r.status === 'match_code' || r.status === 'match_tax'),
      ambiguous: preview.rows.filter((r) => r.status === 'ambiguous'),
      unmatched: preview.rows.filter((r) => r.status === 'unmatched' || r.status === 'link_missing'),
      linked: preview.rows.filter((r) => r.status === 'linked'),
    }),
    [preview],
  );
  const count = Object.values(chosen).filter(Boolean).length;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      variant="sheet"
      title={t('admin.bizimhesap.importCustomers')}
      description={t('admin.bizimhesap.previewSummary', {
        matched: preview.counts.matched,
        ambiguous: preview.counts.ambiguous,
        unmatched: preview.counts.unmatched,
        linked: preview.counts.linked,
        other: preview.counts.otherCustomers,
      })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={apply.isPending}
            disabled={!count}
            onClick={() => apply.mutate(undefined)}
          >
            {t('admin.bizimhesap.applyLinks', { count })}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {groups.matched.length > 0 && (
          <fieldset className="space-y-1.5">
            <legend className="mb-1 text-sm font-medium">{t('admin.bizimhesap.groupMatched')}</legend>
            {groups.matched.map((r) => (
              <label key={r.companyId} className="flex flex-wrap items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-[var(--zt-pine)]"
                  checked={!!chosen[r.companyId]}
                  onChange={(e) =>
                    setChosen({ ...chosen, [r.companyId]: e.target.checked ? r.candidate!.code! : '' })
                  }
                />
                <span className="font-medium">{r.legalName}</span>
                <span className="text-ink-3">→</span>
                <span>
                  {r.candidate!.title} ({r.candidate!.code})
                </span>
                <Badge tone="slate">
                  {t(r.status === 'match_code' ? 'admin.bizimhesap.byCode' : 'admin.bizimhesap.byTax')}
                </Badge>
              </label>
            ))}
          </fieldset>
        )}
        {groups.ambiguous.length > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-medium">{t('admin.bizimhesap.groupAmbiguous')}</p>
            {groups.ambiguous.map((r) => (
              <Field key={r.companyId} label={r.legalName} help={<HelpTip k="bizimhesap.ambiguous" />}>
                {(p) => (
                  <Select
                    {...p}
                    value={chosen[r.companyId] ?? ''}
                    onChange={(e) => setChosen({ ...chosen, [r.companyId]: e.target.value })}
                  >
                    <option value="">{t('admin.bizimhesap.leaveUnlinked')}</option>
                    {r.candidates.map((c) => (
                      <option key={c.code ?? ''} value={c.code ?? ''}>
                        {c.title} ({c.code})
                      </option>
                    ))}
                  </Select>
                )}
              </Field>
            ))}
          </div>
        )}
        {groups.unmatched.length > 0 && (
          <div>
            <p className="text-sm font-medium">{t('admin.bizimhesap.groupUnmatched')}</p>
            <p className="text-sm text-ink-3">{t('admin.bizimhesap.unmatchedLead')}</p>
            <ul className="mt-1 list-disc ps-5 text-sm">
              {groups.unmatched.map((r) => (
                <li key={r.companyId}>
                  {r.legalName}
                  {r.status === 'link_missing' &&
                    ` — ${t('admin.bizimhesap.linkMissing', { code: r.candidate?.code ?? '' })}`}
                </li>
              ))}
            </ul>
          </div>
        )}
        {groups.linked.length > 0 && (
          <p className="text-sm text-ink-3">
            {t('admin.bizimhesap.alreadyLinked', { count: groups.linked.length })}
          </p>
        )}
      </div>
    </Dialog>
  );
}

function WritesSection({ data }: { data: BizimhesapSettings }) {
  const t = useT();
  const { can } = usePermissions();
  const allowed = can('org.integration.go_live');
  const [w, setW] = useState({
    collections: data.writes.collections,
    customers: data.writes.customers,
    cancel: data.writes.cancel,
  });
  const [ack, setAck] = useState(false);
  const turningOn =
    (w.collections && !data.writes.collections) ||
    (w.customers && !data.writes.customers) ||
    (w.cancel && !data.writes.cancel);
  const changed =
    w.collections !== data.writes.collections ||
    w.customers !== data.writes.customers ||
    w.cancel !== data.writes.cancel;
  const save = useApiMutation(
    () =>
      api.put('/v1/admin/bizimhesap/writes', {
        ...w,
        ...(turningOn ? { confirm: ack } : {}),
        version: data.version,
      }),
    { invalidate: [['bizimhesap']], success: t('common.saved'), silentFieldErrors: true },
  );
  return (
    <Section
      title={t('admin.bizimhesap.writesTitle')}
      description={t('admin.bizimhesap.writesLead')}
      action={<HelpTip k="bizimhesap.writes" />}
    >
      <div className="space-y-3">
        {(['collections', 'customers', 'cancel'] as const).map((k) => (
          <div key={k} className="flex items-center gap-2">
            <Switch
              label={t(`admin.bizimhesap.writeKeys.${k}`)}
              checked={w[k]}
              disabled={!allowed}
              onCheckedChange={(v) => {
                setW({ ...w, [k]: v });
                setAck(false);
              }}
            />
            <span className="text-sm">{t(`admin.bizimhesap.writeKeys.${k}`)}</span>
            <HelpTip k={`bizimhesap.write_${k}`} />
          </div>
        ))}
      </div>
      {turningOn && (
        <label className="mt-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-1 accent-[var(--zt-pine)]"
            checked={ack}
            onChange={(e) => setAck(e.target.checked)}
          />
          <span>{t('admin.bizimhesap.writesConfirm')}</span>
        </label>
      )}
      {fieldError(save.error, 'confirm') && (
        <p role="alert" className="mt-2 text-sm text-rose">
          {fieldError(save.error, 'confirm')}
        </p>
      )}
      {allowed ? (
        <div className="mt-4 flex items-center gap-1">
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={!changed || (turningOn && !ack)}
            onClick={() => save.mutate(undefined)}
          >
            {t('admin.bizimhesap.saveWrites')}
          </Button>
          <HelpTip k="bizimhesap.save_writes" />
        </div>
      ) : (
        <p className="mt-3 text-sm text-ink-3">{t('admin.bizimhesap.writesOwnerOnly')}</p>
      )}
    </Section>
  );
}

function CorrectionsSection({ data }: { data: BizimhesapSettings }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const manage = can('b2b.ledger.manage');
  const q = useQuery({
    queryKey: ['bizimhesap-corrections'],
    queryFn: () => api.get<BizimhesapCorrection[]>('/v1/admin/bizimhesap/corrections'),
    enabled: can('invoices.invoice.read'),
  });
  const [picked, setPicked] = useState<string[]>([]);
  const queue = useApiMutation(
    () =>
      api.post<{ created: number }>('/v1/admin/bizimhesap/corrections/collections', { invoiceIds: picked }),
    {
      invalidate: [['bizimhesap-corrections'], ['bizimhesap-collections'], ['bizimhesap']],
      onSuccess: (r) => {
        toast.success(t('admin.bizimhesap.queued', { count: r.created }));
        setPicked([]);
      },
    },
  );
  if (!can('invoices.invoice.read')) return null;
  const open = (q.data ?? []).filter((r) => !r.collection);
  return (
    <Section
      title={t('admin.bizimhesap.correctionsTitle')}
      description={t('admin.bizimhesap.correctionsLead')}
      action={<HelpTip k="bizimhesap.corrections" />}
    >
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data?.length ? (
        <p className="text-sm text-ink-3">{t('admin.bizimhesap.noCorrections')}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-line">
            <table
              className="w-full min-w-[44rem] text-sm"
              aria-label={t('admin.bizimhesap.correctionsTitle')}
            >
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  {manage && <th className="w-8 px-3 py-2" />}
                  <th className="px-3 py-2 text-start font-medium">{t('admin.orders.number')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.method')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.customerCode')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.sentAt')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.bizimhesap.amount')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.collection')}</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((r) => (
                  <tr key={r.invoiceId} className="border-t border-line">
                    {manage && (
                      <td className="px-3 py-2">
                        {!r.collection && (
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[var(--zt-pine)]"
                            aria-label={t('admin.bizimhesap.pickCorrection', { number: r.orderNumber })}
                            checked={picked.includes(r.invoiceId)}
                            onChange={(e) =>
                              setPicked(
                                e.target.checked
                                  ? [...picked, r.invoiceId]
                                  : picked.filter((x) => x !== r.invoiceId),
                              )
                            }
                          />
                        )}
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <Link
                        to="/siparisler/$id"
                        params={{ id: r.orderId }}
                        className="text-pine hover:underline"
                      >
                        {r.orderNumber}
                      </Link>
                    </td>
                    <td className="px-3 py-2">
                      {t(`admin.bizimhesap.cashKeys.${r.method}`)}
                      {!r.cashId && (
                        <span className="block text-xs text-rose">{t('admin.bizimhesap.cashMissing')}</span>
                      )}
                    </td>
                    <td className="px-3 py-2">{r.customerCode ?? '—'}</td>
                    <td className="px-3 py-2 text-ink-2">{r.sentAt ? formatDate(r.sentAt, true) : '—'}</td>
                    <td className="px-3 py-2 text-end tabular">{money(r.amountMinor, r.currency)}</td>
                    <td className="px-3 py-2">
                      {r.collection ? (
                        <Badge tone={collectionTone[r.collection.status]}>
                          {r.collection.number} · {t(`admin.bizimhesap.statuses.${r.collection.status}`)}
                        </Badge>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {manage && open.length > 0 && (
            <div className="mt-3 flex items-center gap-1">
              <Button
                loading={queue.isPending}
                disabled={!picked.length}
                onClick={() => queue.mutate(undefined)}
              >
                {t('admin.bizimhesap.queueCorrections', { count: picked.length })}
              </Button>
              <HelpTip k="bizimhesap.queue_corrections" />
            </div>
          )}
          {!data.writes.collections && (
            <p className="mt-2 text-sm text-ink-3">{t('admin.bizimhesap.writesOffNote')}</p>
          )}
        </>
      )}
    </Section>
  );
}

function CollectionsSection({ data }: { data: BizimhesapSettings }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const manage = can('b2b.ledger.manage');
  const q = useQuery({
    queryKey: ['bizimhesap-collections'],
    queryFn: () => api.get<BizimhesapCollection[]>('/v1/admin/bizimhesap/collections'),
    enabled: can('b2b.ledger.read'),
  });
  const [idem] = useState(() => new IdempotencyKey());
  const [cancelling, setCancelling] = useState<BizimhesapCollection | null>(null);
  const invalidate = [['bizimhesap-collections'], ['bizimhesap-corrections'], ['bizimhesap']];
  const send = useApiMutation(
    (id: string) =>
      api.post<BizimhesapCollection>(
        `/v1/admin/bizimhesap/collections/${id}/send`,
        {},
        { idempotencyKey: idem },
      ),
    {
      invalidate,
      onSuccess: (r) =>
        r.status === 'sent'
          ? toast.success(t('admin.bizimhesap.sent'))
          : toast.warning(
              t(`admin.bizimhesap.sendResult.${r.status === 'uncertain' ? 'uncertain' : 'failed'}`),
            ),
    },
  );
  const check = useApiMutation(
    (id: string) =>
      api.post<BizimhesapCollection>(`/v1/admin/bizimhesap/collections/${id}/resolve`, { outcome: 'check' }),
    {
      invalidate,
      onSuccess: (r) =>
        toast.success(
          r.status === 'sent' ? t('admin.bizimhesap.checkFound') : t('admin.bizimhesap.checkNotFound'),
        ),
    },
  );
  if (!can('b2b.ledger.read')) return null;
  return (
    <Section
      title={t('admin.bizimhesap.collectionsTitle')}
      description={t('admin.bizimhesap.collectionsLead')}
      action={<HelpTip k="bizimhesap.collections" />}
    >
      {q.isLoading ? (
        <Spinner label={t('common.loading')} />
      ) : !q.data?.length ? (
        <p className="text-sm text-ink-3">{t('admin.bizimhesap.noCollections')}</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[46rem] text-sm" aria-label={t('admin.bizimhesap.collectionsTitle')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.collectionNumber')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.account')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.paymentType')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.bizimhesap.amount')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.bizimhesap.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {q.data.map((c) => (
                <tr key={c.id} className="border-t border-line">
                  <td className="px-3 py-2">
                    <span className="font-medium">{c.number}</span>
                    <span className="block text-xs text-ink-3">
                      {c.orderNumber
                        ? `${t(`admin.bizimhesap.sources.${c.source}`)} · ${c.orderNumber}`
                        : t(`admin.bizimhesap.sources.${c.source}`)}
                    </span>
                  </td>
                  <td className="px-3 py-2">
                    {c.legalName ?? c.customerCode}
                    <span className="block text-xs text-ink-3">{c.customerCode}</span>
                  </td>
                  <td className="px-3 py-2">
                    {t(`admin.bizimhesap.paymentTypes.${c.paymentType as 1 | 2 | 3}`)}
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(c.amountMinor, c.currency)}</td>
                  <td className="px-3 py-2">
                    <Badge tone={collectionTone[c.status]}>
                      {t(`admin.bizimhesap.statuses.${c.status}`)}
                    </Badge>
                    {c.sentAt && (
                      <span className="block text-xs text-ink-3">{formatDate(c.sentAt, true)}</span>
                    )}
                    {(c.error || c.resolvedNote) && (
                      <span className="block text-xs text-ink-3">{c.resolvedNote ?? c.error}</span>
                    )}
                    {c.ledgerReversed && c.status !== 'cancelled' && (
                      <span className="block text-xs text-rose">{t('admin.bizimhesap.reversedFixAtBh')}</span>
                    )}
                    {!c.cashId && (c.status === 'pending' || c.status === 'failed') && (
                      <span className="block text-xs text-rose">{t('admin.bizimhesap.cashMissing')}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-end">
                    {manage && (
                      <span className="inline-flex flex-wrap justify-end gap-1">
                        {(c.status === 'pending' || c.status === 'failed') && (
                          <>
                            <Button
                              size="sm"
                              disabled={!data.writes.collections}
                              title={
                                data.writes.collections ? undefined : t('admin.bizimhesap.writesOffNote')
                              }
                              loading={send.isPending}
                              onClick={() => send.mutate(c.id)}
                            >
                              {t('admin.bizimhesap.send')}
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setCancelling(c)}>
                              {t('admin.bizimhesap.cancelCollection')}
                            </Button>
                          </>
                        )}
                        {(c.status === 'uncertain' || c.status === 'sending') && (
                          <Button size="sm" loading={check.isPending} onClick={() => check.mutate(c.id)}>
                            {t('admin.bizimhesap.checkStatement')}
                          </Button>
                        )}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {manage && (
        <p className="mt-2 flex items-center gap-1 text-sm text-ink-3">
          {t('admin.bizimhesap.collectionActionsNote')} <HelpTip k="bizimhesap.collection_actions" />
        </p>
      )}
      {cancelling && <CancelCollectionDialog collection={cancelling} onClose={() => setCancelling(null)} />}
    </Section>
  );
}

function CancelCollectionDialog({
  collection,
  onClose,
}: {
  collection: BizimhesapCollection;
  onClose: () => void;
}) {
  const t = useT();
  const [note, setNote] = useState('');
  const cancel = useApiMutation(
    () => api.post(`/v1/admin/bizimhesap/collections/${collection.id}/cancel`, { note: note.trim() }),
    {
      invalidate: [['bizimhesap-collections'], ['bizimhesap-corrections'], ['bizimhesap']],
      success: t('admin.bizimhesap.cancelled'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      title={t('admin.bizimhesap.cancelTitle', { number: collection.number })}
      description={t('admin.bizimhesap.cancelLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={cancel.isPending}
            disabled={note.trim().length < 3}
            onClick={() => cancel.mutate(undefined)}
          >
            {t('admin.bizimhesap.cancelCollection')}
          </Button>
        </>
      }
    >
      <Field
        label={t('admin.bizimhesap.cancelNote')}
        required
        help={<HelpTip k="bizimhesap.cancel_note" />}
        error={fieldError(cancel.error, 'note')}
      >
        {(p) => (
          <Textarea {...p} className="min-h-16" value={note} onChange={(e) => setNote(e.target.value)} />
        )}
      </Field>
    </Dialog>
  );
}

function SamplesSection({ data }: { data: BizimhesapSettings }) {
  const t = useT();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const manage = can('org.integration.manage');
  const reset = useApiMutation((endpoint: string) => api.del(`/v1/admin/bizimhesap/samples/${endpoint}`), {
    invalidate: [['bizimhesap']],
    success: t('admin.bizimhesap.sampleReset'),
  });
  const entries = (['customers', 'abstract', 'cashiers'] as const).map((k) => [k, data.samples[k]] as const);
  return (
    <Section
      title={t('admin.bizimhesap.samplesTitle')}
      description={t('admin.bizimhesap.samplesLead')}
      action={<HelpTip k="bizimhesap.samples" />}
    >
      <div className="space-y-3">
        {entries.map(([k, s]) => (
          <details key={k} className="rounded-md border border-line px-3 py-2">
            <summary className="cursor-pointer text-sm font-medium">
              {t(`admin.bizimhesap.endpoints.${k}`)} —{' '}
              {s
                ? t('admin.bizimhesap.sampleAt', { date: formatDate(s.capturedAt, true) })
                : t('admin.bizimhesap.sampleNone')}
            </summary>
            {s && (
              <>
                <pre
                  className="mt-2 max-h-80 overflow-auto rounded bg-surface-2 p-2 text-xs"
                  aria-label={t(`admin.bizimhesap.endpoints.${k}`)}
                >
                  {JSON.stringify(s.body, null, 2)}
                </pre>
                <div className="mt-2 flex gap-1">
                  <Button
                    size="sm"
                    onClick={() =>
                      void navigator.clipboard
                        .writeText(JSON.stringify(s.body, null, 2))
                        .then(() => toast.success(t('admin.bizimhesap.copied')))
                    }
                  >
                    {t('admin.bizimhesap.copySample')}
                  </Button>
                  {manage && (
                    <Button
                      size="sm"
                      variant="ghost"
                      loading={reset.isPending}
                      onClick={() => reset.mutate(k)}
                    >
                      {t('admin.bizimhesap.resetSample')}
                    </Button>
                  )}
                </div>
              </>
            )}
          </details>
        ))}
      </div>
    </Section>
  );
}
